import { useEffect, useState, useRef } from 'react';
import styles from './styles/countertops.module.css'
import imageData from '../../data/Services/CountertopData.json';
import FAB from '../../helpers/fab'

const useSwipe = (onSwipeLeft, onSwipeRight) => {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const onTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > minSwipeDistance) {
      distance > 0 ? onSwipeLeft() : onSwipeRight();
    }
  };

  return { onTouchStart, onTouchEnd };
};

const Countertops = () => {
  const [countertopData, setCountertopData] = useState([]);
  const [activeTypeIndex, setActiveTypeIndex] = useState(0);
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [modalState, setModalState] = useState({ images: [], index: null });

  useEffect(() => {
    setCountertopData(imageData);
    if (imageData.length > 0) {
      setActiveSupplier(null);
    }
  }, []);

  const activeType = countertopData[activeTypeIndex];
  const suppliers =
    activeType?.options && activeType.options.some((o) => o.supplier)
      ? [...new Set(activeType.options.map((o) => o.supplier).filter(Boolean))]
      : [];

  const filteredOptions =
    activeType?.options.filter((o) => (activeSupplier ? o.supplier === activeSupplier : true)) || [];

  const openModal = (index) => {
    setModalState({ images: filteredOptions, index });
  };

  const closeModal = () => {
    setModalState({ images: [], index: null });
  };

  const prevImage = () => {
    setModalState((prev) => ({
      ...prev,
      index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1,
    }));
  };

  const nextImage = () => {
    setModalState((prev) => ({
      ...prev,
      index: (prev.index + 1) % prev.images.length,
    }));
  };

  const isModalOpen = modalState.index !== null;
  const swipeHandlers = useSwipe(nextImage, prevImage);

  useEffect(() => {
    const handleKey = (e) => {
      if (!isModalOpen) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isModalOpen]);

  // // Handlers for dropdown changes on mobile
  // const onTypeChange = (e) => {
  //   const index = countertopData.findIndex((c) => c.type === e.target.value);
  //   if (index !== -1) {
  //     setActiveTypeIndex(index);
  //     setActiveSupplier(null);
  //   }
  // };

  // const onSupplierChange = (e) => {
  //   setActiveSupplier(e.target.value || null);
  // };

  return (
    <section className={styles.countertops}>
      <h2>Explore Our Countertops</h2>
      <div className={styles.countertopLayout}>
        <div className={styles.tabsContainer}>

          {/* LEFT SIDE TABS - hidden on mobile via CSS */}
          <aside className={styles.tabsNav}>
            {countertopData.map((group, i) => (
              <div key={group.type}>
                <button
                  className={`${styles.tabButton} ${i === activeTypeIndex ? styles.active : ''}`}
                  onClick={() => {
                    setActiveTypeIndex(i);
                    setActiveSupplier(null);
                  }}
                >
                  {group.type}
                </button>

                {i === activeTypeIndex && suppliers.length > 0 && (
                <div className={styles.supplierSubtabs}>
                  {/* "All Brands" tab */}
                  {suppliers.length > 1 && <button
                    className={`${styles.supplierTab} ${activeSupplier === null ? styles.active : ''}`}
                    onClick={() => setActiveSupplier(null)}
                  >
                    All Brands
                  </button>}

                  {/* individual suppliers */}
                  {suppliers.length > 1 &&
                    suppliers.map((supplier) => {
                      const isSupplierCanadian = activeType.options.some(
                        (o) => o.supplier === supplier && o.isCanadian
                      );

                      return (
                        <button
                          key={supplier}
                          className={`${styles.supplierTab} ${supplier === activeSupplier ? styles.active : ''}`}
                          onClick={() => setActiveSupplier(supplier)}
                        >
                          {supplier}
                          {isSupplierCanadian && (
                            <img
                              src="https://ik.imagekit.io/1stopkitchens/images/canadian_icon.png"
                              alt="Canadian"
                              className={styles.canadianTabIcon}
                            />
                          )}
                        </button>
                      );
                    })
                  }
                </div>
              )}
              </div>
            ))}
          </aside>

          {/* RIGHT SIDE CONTENT */}
          <div className={styles.tabContent}>
            <div className={styles.typeDescriptionContainer}>
              <p className={styles.primaryDescription}>{activeType?.description}</p>
              {activeType?.subdescription !== null && <p className={styles.secondaryDescription}>{activeType?.subdescription}</p>}
              {filteredOptions?.[0]?.supplier === 'HanStone' && (
                <p className={styles.proudlyCanadian}>
                  <img src="https://ik.imagekit.io/1stopkitchens/images/canadian_icon.png" alt="Canadian" className={styles.canadianIcon} />
                  The HanStone products below are proudly canadian made!
                </p>
              )}
            </div>
            <div className={styles.countertopGallery}>
              {filteredOptions?.map((option, idx) => (
                <div className={styles.countertopCard} key={idx} onClick={() => openModal(idx)}>
                  <div className={`${styles.countertopCardImage} ${option.isBudgetFriendly ? styles.budgetFriendly : ''}`} >
                  {option.isBudgetFriendly && <span className={styles.budgetFriendlyText}>Budget Friendly</span>}
                    {option.isPopular && <span className={styles.popular}>Popular right now!</span>}
                    <img src={option.image} alt={option.name} />
                  </div>
                  <div className={styles.overlay}>{option.name}</div>
                  {option.isCanadian && (
                    <div className={styles.canadianBadge}>
                      <img src="https://ik.imagekit.io/1stopkitchens/images/canadian_icon.png" alt="Canadian" className={styles.canadianIcon} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal} {...swipeHandlers}>
          <div className="modal-content countertop-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close">
              ×
            </button>
            <p className="modal-image-caption">
              {modalState.images[modalState.index].name}
              {modalState.images[modalState.index].isCanadian && (
                <img
                  src="https://ik.imagekit.io/1stopkitchens/images/canadian_icon.png"
                  alt="Canadian"
                  className="canadian-icon"
                />
              )}
            </p>
            <div 
              className={
                  `modal-image-wrapper 
                  ${modalState.images[modalState.index].isBudgetFriendly ? 'budget-friendly' : ''}
                  ${modalState.images[modalState.index].moreInfo  ? 'has-zoom' : 'no-zoom'}
                `}
              >
              {modalState.images[modalState.index].isPopular && (
                <span className="modal-popular">Popular right now!</span>
              )}
              {modalState.images[modalState.index].isBudgetFriendly && (
                <span className="modal-budget-friendly-text">Budget Friendly</span>
              )}
              {modalState.images[modalState.index].moreInfo ? (
                <a href={modalState.images[modalState.index].moreInfo} target="_blank" rel="noreferrer" className="modal-link">
                  <img
                    src={modalState.images[modalState.index].image}
                    alt={modalState.images[modalState.index].name}
                    className="modal-image"
                  />
                </a>
              ) : (
                <img
                  src={modalState.images[modalState.index].image}
                  alt={modalState.images[modalState.index].name}
                  className="modal-image"
                />
              )}
            </div>
            {modalState.images[modalState.index].moreInfo && <p className="more-info-text">
              <i>Click on the image above to see more information about this colour</i>
            </p>}
            <div className="modal-nav-buttons">
              <button className="nav-btn prev-btn" onClick={prevImage} aria-label="Previous image">
                ‹
              </button>
              <button className="nav-btn next-btn" onClick={nextImage} aria-label="Next image">
                ›
              </button>
            </div>
          </div>
        </div>
      )}
      
      {!isModalOpen &&<FAB />}
    </section>
  );
};

export default Countertops;